import React, { useRef } from "react";
import { Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

import Divider from "@mui/material/Divider";
import "../JaldiLab.css";
import {
  getTextContentHeading,
  referenceHtml,
  referenceTootKit,
  generateUnOrderedList,
  sectionStyle,
  h4style,
  h1style,
  h3style,
  h2style,
  paraStyle,
  dimensionStyle,
  referenceStyle,
  tableCaption,
  subHeadingContent,
  generateTableUnOrderedList,
} from "./ChapterList";
import { tableCellClasses } from "@mui/material/TableCell";
import objective from "../../../assets/SvgIcons/obj.svg";
import user from "../../../assets/SvgIcons/users.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import users from "../../../assets/SvgIcons/users.svg";

const specialBorderColor = "#F3F3F4";
const borderColor = "#AAAAAA";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#F45B69",
    color: theme.palette.common.white,
    fontWeight: 700,
    fontSize: "16px",
    border: `1px solid ${borderColor}`,
    verticalAlign: "baseline",
  },
  [`&.${tableCellClasses.body}`]: {
    fontWeight: 400,
    fontSize: "14px",
    color: "#555555",
    border: `1px solid ${borderColor}`,
    verticalAlign: "baseline",
    lineHeight: "20px",
    textAlign: "left !important",
  },
  "&.bold-text": {
    fontWeight: 700,
  },
  "&:first-child": {
    borderLeft: `1px solid ${specialBorderColor}`,
  },
  "&:last-child": {
    borderRight: `1px solid ${specialBorderColor}`,
  },
  fontFamily: "Lato",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: "#CCE5E5",
  },
}));

const AdvocatesBarAssociation = ({ goToSection }) => {
  const referencesRef = useRef(null);

  const referenceData = [
    {
      content: "NCMS (2012).",
    },
    {
      content: "Advocates Act 1961.",
    },
  ];

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const ObjectivesObject = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        <span style={{ color: "#DC002D" }}>Ensure comfort for the users</span>{" "}
        by providing segregated Bar Rooms for male and female Advocates,
        {reference(referenceData[0], 1)} ergonomic accessible furniture, and
        adequate sanitary provisions;
      </>,
      <>
        <span style={{ color: "#DC002D" }}>Plan for optimal use of space</span>{" "}
        within the Bar Rooms for all members;
      </>,
      <>
        <span style={{ color: "#DC002D" }}>Ensure access for all</span> by
        providing lifts and escalators at strategic locations, as well as
        appropriate signages and information systems;
      </>,
      <>
        <span style={{ color: "#DC002D" }}>Provide adequate facilities</span> to
        ensure that Advocates may work comfortably as well as rest (when
        necessary); and
      </>,
      <>
        <span style={{ color: "#DC002D" }}>
          Consider the needs of the diverse litigants and general public
        </span>{" "}
        that may visit the space to interact with the Advocates.
      </>,
    ],
    isImage: true,
  };

  const UsersObject = {
    icon: <img src={users} />,
    heading: "Users",
    data: [
      "Advocates",
      "Clerks",
      "Support Staff",
      "Litigants and General Public (for consulting)",
      "Notaries, Typists and Oath Commissioners",
    ],
    isImage: true,
  };

  const AdjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "The Bar Association must be its own self-sustained block.",
      "It may not be part of the general flow of functions in the Court Complex. However, it must be in proximity to the Court Complex to ensure that the Advocates and their staff have easy access to it.",
      "The Bar Association may have its own canteen or must be in close proximity to a canteen.",
      "Access to facilities such as a library, printing facilities, and other supplies including all reprographic services must be provided. ",
    ],
    isImage: true,
  };

  const AccessibilityTable = () => {
    return (
      <div style={{ margin: "30px 0" }}>
        <p className="table-guidance-heading">
          <span className="table-guidline-text">Table 9:</span>
          <br />
          <span style={tableCaption}>
            Design considerations arising from the Advocates Act
          </span>
        </p>

        <TableContainer
          component={Paper}
          className="advocatesTable"
          style={{ borderLeft: "#F3F3F4" }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  color: "white !important",
                  backgroundColor: "#F45B69",
                  borderLeft: "#F3F3F4",
                }}
              >
                <StyledTableCell>Section No.</StyledTableCell>
                <StyledTableCell>Section Title</StyledTableCell>
                <StyledTableCell>Section Relevant Details</StyledTableCell>
                <StyledTableCell>Design Considerations</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <StyledTableRow>
                <StyledTableCell style={{ textAlign: "center" }}>
                  S.5
                </StyledTableCell>
                <StyledTableCell style={{ textAlign: "center" }}>
                  Bar Council to be body corporate
                </StyledTableCell>
                <StyledTableCell style={{ textAlign: "center" }}>
                  Every Bar Council shall be a body corporate having perpetual
                  succession and a common seal, with power to acquire and hold
                  property, both moveable and immovable, and to contract, and
                  may by the name by which it is known sue and be sued.
                </StyledTableCell>
                <StyledTableCell style={{ textAlign: "center" }}>
                  {generateTableUnOrderedList([
                    "The Bar Council must have its own office premises.",
                  ])}
                </StyledTableCell>
              </StyledTableRow>

              <StyledTableRow>
                <StyledTableCell style={{ textAlign: "center" }}>
                  S.6
                </StyledTableCell>
                <StyledTableCell style={{ textAlign: "center" }}>
                  Functions of State Bar Councils
                </StyledTableCell>
                <StyledTableCell style={{ textAlign: "center" }}>
                  <span style={sectionStyle}>(2)</span> A State Bar Council may
                  constitute one or more funds in the prescribed manner for the
                  purpose of—
                  <p>
                    {" "}
                    <span style={sectionStyle}>(c)</span> establishing law
                    libraries.
                  </p>
                </StyledTableCell>
                <StyledTableCell style={{ textAlign: "center" }}>
                  {generateTableUnOrderedList([
                    "There must be a supporting library equipped with a reading room, provisions to access online journals, etc., as part of the Bar Association rooms.",
                  ])}
                </StyledTableCell>
              </StyledTableRow>

              <StyledTableRow>
                <StyledTableCell style={{ textAlign: "center" }}>
                  S.11
                </StyledTableCell>
                <StyledTableCell style={{ textAlign: "center" }}>
                  Staff of Bar Council
                </StyledTableCell>
                <StyledTableCell style={{ textAlign: "center" }}>
                  <span style={sectionStyle}>(1)</span> Every Bar Council shall
                  appoint a Secretary and may appoint an accountant and such
                  number of other persons on its staff as it may deem necessary.
                </StyledTableCell>
                <StyledTableCell style={{ textAlign: "center" }}>
                  {generateTableUnOrderedList([
                    "The Bar Association must have adequate rooms for the Secretary, accountant and other office holders of the respective Associations.",
                  ])}
                </StyledTableCell>
              </StyledTableRow>

              <StyledTableRow>
                <StyledTableCell style={{ textAlign: "center" }}>
                  S.25
                </StyledTableCell>
                <StyledTableCell style={{ textAlign: "center" }}>
                  Authority to whom applications for enrolment may be made
                </StyledTableCell>
                <StyledTableCell style={{ textAlign: "center" }}>
                  An application for admission as an advocate shall be made in
                  the prescribed form to the State Bar Council within whose
                  jurisdiction the applicant proposes to practise.
                </StyledTableCell>
                <StyledTableCell style={{ textAlign: "center" }}>
                  {generateTableUnOrderedList([
                    "The Bar Association must also have a dedicated room with a counter space for new advocates to apply for enrollment.",

                    "The Enrollment counter must be accessible for diverse users and must have sufficient informational pamphlets/ kiosks to provide information regarding the process of enrollment.",
                  ])}
                </StyledTableCell>
              </StyledTableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  };

  const DesignConsiderationsObject = [
    {
      icon: <img src={designConsideration} />,
      heading: "Design Considerations",
      items: [
        <>
          The relevant provisions of the Advocates Act (1961) which pertain to
          infrastructural requirements are:{reference(referenceData[1], 2)}
          <AccessibilityTable />
        </>,
        "The Bar Association rooms must be designed keeping in mind the number of members of the Bar Association, with scope to increase the space in the future.",
        "There must be adequate space for all Advocates to have their own work space, along with space for their juniors, interns, and office clerks.",
        "Comfortable and ergonomic furniture must be provided in all spaces within the Bar Association. ",
        "The rooms must have adequate sockets and charging points for the use of laptops, computers, scanners and other electronics, as necessary.",
        "Adequate storage space must also be provided for the storage of case files, books, and personal belongings.",
        "The space must be provided with adequate accessible sanitary provisions for all members of the Association.",
        <>
          Separate accessible sanitary provisions must be provided in the public
          areas for all litigants and the general public visiting the space. For
          further details for the design of Sanitary Provisions, please refer to{" "}
          <span
            style={sectionStyle}
            onClick={() =>
              goToSection("sanitaryProvisions", "sanitaryProvisions")
            }
          >
            {" "}
            Section 3.1.
          </span>
        </>,
      ],
    },
    {
      heading: "Bar Council Counter",
      items: [
        "A dedicated space must be provided for new enrollments to the Bar Council. ",
        "The counter should be accessible to diverse user groups. ",
        "Adequate space must be provided for persons waiting their turn for the enrollment, including adequate ergonomic seating. Facilities must also be provided for the users to be able to fill their forms, print out documents, etc.",
        "The design of the space must be optimised to facilitate smooth movement of users to avoid overcrowding. ",
      ],
    },
    {
      heading: "Meeting Rooms",
      items: [
        "Dedicated meeting space must be provided where the Advocates may be able to undertake consultation with their clients.",
        "The meeting rooms must be secluded from the main area of the Bar Association, to ensure privacy for the litigants visiting for meetings.",
        <>
          The room must be designed to ensure private conversations with
          effective acoustic barriers with soundproof partitions and
          acoustically treated doors. Acoustically absorptive ceiling and wall
          materials must be provided to ensure comfort. For further details on
          acoustics, please refer to{" "}
          <span
            style={sectionStyle}
            onClick={() => goToSection("annexes", "HeadingAnnexure5")}
          >
            {" "}
            Annexure 5.
          </span>
        </>,
      ],
    },
    {
      heading: "Lounge/Canteen Area",
      items: [
        "The Bar Association must have a lounge area for the Advocates to relax/have discussions with their peers. ",
        "The Bar Association may have its dedicated canteen/pantry area with seating for Advocates or must be in close proximity to the same.",
        "All spaces within the lounge/canteen must be accessible to diverse users with accessible ergonomic furniture and sanitary provisions.",
      ],
    },
    {
      heading: "Additional Spaces",
      items: [
        <>
          Separate office rooms should be provided for the office holders and
          staff members of the Association. For further details of
          administrative office spaces, please refer to{" "}
          <span
            style={sectionStyle}
            onClick={() =>
              goToSection("officeSpaces", "officeSpacesSingleWorkspace")
            }
          >
            {" "}
            Section 2.5.B
          </span>{" "}
          and{" "}
          <span
            style={sectionStyle}
            onClick={() =>
              goToSection("officeSpaces", "officeSpacesMultiPersonWorkspace")
            }
          >
            {" "}
            Section 2.5.C.
          </span>
        </>,
        "Space must also be provided for undertaking meetings for members of the Association, as well as smaller meeting rooms for the Bar Association office holders.",
        <>
          Additional space may be provided for holding conferences and other
          events of the Association such as sensitisation training programs,
          skill-building workshops for the Advocates, etc., For further details
          on the design of conference rooms and training rooms, please refer to{" "}
          <span
            style={sectionStyle}
            onClick={() =>
              goToSection("conferenceMeetingRoom", "HeadingConferenceRoom")
            }
          >
            {" "}
            Section 2.9.B.
          </span>
        </>,
        "Adequate rooms/halls must be provided for notaries, typists, and Oath Commissioners to undertake their functions. ",
        "Adequate space must also be provided to undertake legal aid activities for the general public.",
        "Stationary facilities and reprographic services  must be provided, with all basic supplies that may be necessary on a day-to-day basis.",
      ],
    },
  ];

  const ObjectivesSecondObject = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Design the Chambers to{" "}
        <span style={{ color: "#DC002D" }}> streamline processes </span> for the
        effective execution of legal proceedings;
      </>,
      <>
        Ensure{" "}
        <span style={{ color: "#DC002D" }}>
          swift response, efficient coordination, and confidentiality{" "}
        </span>
        , when necessary, by locating the PP/GP Chambers to be in close
        proximity to Courtrooms;
      </>,
      <>
        Ensure that the{" "}
        <span style={{ color: "#DC002D" }}>
          office spaces are accessible to diverse user groups{" "}
        </span>
        of lawyers, litigants and staff who may use the space;
      </>,
      <>
        Provide a spacious, private waiting area prioritising the
        <span style={{ color: "#DC002D" }}>
          {" "}
          comfort and privacy of all litigants{" "}
        </span>
        visiting the PP/GP Chambers; and
      </>,
      <>
        Ensure provision of{" "}
        <span style={{ color: "#DC002D" }}>
          adequate, accessible sanitary provisions
        </span>{" "}
        for diverse user groups.
      </>,
    ],
  };

  const UsersSecondObject = {
    icon: <img src={user} />,
    heading: "Users",
    data: [
      "Public Prosecutor / Government Pleader",
      "Assistant Public Prosecutor / Assistant Government Pleader (if applicable)",
      "Empanelled Advocates",
      "Office staff",
      "Litigants",
    ],
  };

  const AdjacenciesSecondObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "The Chambers for the PP / GP must be strategically located near or in close proximity to the Courtrooms, or have segregated access to the Courtrooms in order to ensure direct access whenever necessary.",
      "Office spaces for empanelled lawyers and staff must be in close proximity to the Chambers for the PP / GP, to ensure seamless coordination and efficient functioning.",
    ],
  };

  const DesignConsiderationsSecondObject = {
    icon: <img src={designConsideration} />,
    heading: "Design Considerations",
    data: [
      "The Chambers for the PP / GP must be designed to be a self-contained space for the officers, their staff, and other empanelled lawyers.",
      "A designated waiting area must be provided at the entrance of the Chambers, or in close proximity, to ensure that visiting litigants have a designated seating space.",
      "Adequate designated space must be provided within the Chambers or in close proximity for office staff and other empanelled lawyers.",
      "The offices must have dedicated accessible sanitary provisions, or be in close proximity to the same.",
      "All spaces in the Chambers must be equipped with modular seating, and provision should be made for natural light and ventilation, including well-designed acoustics.",
      "The Chambers must have adequate compact storage for all the case files being dealt with.",
    ],
  };

  const OFficesppgpObject = {
    heading: "Offices of the PP / GP",
    data: [
      "Separate cubicles must be provided for the PP and the GP, wherever possible.",
      "The rooms must be designed to have adequate work space, as well as storage for the files and personal belongings of the PP/GP.",
      "The rooms must have adequate ergonomic seating for litigants visiting the space.",
      "The room must be designed to ensure private conversations with effective acoustic barriers, including soundproof partitions, acoustic doors, acoustically absorptive ceiling, and wall materials.",
    ],
  };

  const WaitingAreaObject = {
    heading: "Waiting Area",
    data: [
      "Adequate space must be provided for a separate, dedicated waiting area for litigants visiting the PP / GP.",
      "The waiting area must be accessible to diverse user groups with adequate ramps and elevators, accessible signage, and tactile paths.",
      <>
        Provision must be made for accessible case display screens and
        information kiosks with audible announcements. For further details on
        the design of the waiting area, refer to
        <span
          onClick={() => goToSection("waitingArea", "headingWaitingAreas")}
          style={sectionStyle}
        >
          {" "}
          Section 2.2.
        </span>
      </>,
    ],
  };

  const SpaceForOfficeClerksObject = {
    heading: "Space for Office Clerks/Secretaries",
    data: [
      "Adequate space must be provided for the offices of the secretarial staff attached with each PP / GP.",
      "Adequate ergonomic seating and technology must be provided, for the various functions to be undertaken by the staff.",
      "Adequate storage facilities must be provided to store official documents as well as personal belonging of the staff.",
    ],
  };

  const SpaceForEmpanelledAdvocatesObject = {
    heading: "Space for Empanelled Advocates",
    data: [
      <>
        Separate workspace must be provided for empanelled Advocates. For
        further details on design of a multi-person office space, refer to{" "}
        <span
          onClick={() =>
            goToSection("officeSpaces", "officeSpacesMultiPersonWorkspace")
          }
          style={sectionStyle}
        >
          {" "}
          Section 2.5.C.
        </span>
      </>,
      "These rooms must be equipped with a case display system (with audible announcements) and kiosks, to ensure that the Advocates are able to access information regarding ongoing cases conveniently.",
      "Adequate functional work spaces, with ergonomic furniture for Advocates and other staff to undertake their work, must be provided.",
    ],
  };

  const LibraryObject = {
    heading: "Library",
    data: [
      "The Chambers of the PP / GP must be equipped with requisite library facilities.",
      "The library must be well-equipped with IT infrastructure, to ensure smooth functioning and access to sufficient resources for research and legal reference.",
      <>
        The library must have sufficient provision of ergonomic chairs, desks,
        storage cupboards, planned electrical points, fans, etc., For further
        details on the design of the library, refer to
        <span
          onClick={() => goToSection("library", "headingLibrary")}
          style={sectionStyle}
        >
          {" "}
          Section 2.9.C.
        </span>
      </>,
    ],
  };

  const tableStyle = {
    fontFamily: "Lato",
    fontSize: "18px",
    fontWeight: "400",
    lineHeight: "27px",
    color: "#555555",
    letterSpacing: "0em",
    textAlign: "justified",
    margin: 0,
  };
  const tableHeading = {
    color: "#000",
    fontSize: "1rem",
    fontWeight: 600,
    paddingRight: "5px",
  };

  return (
    <div>
      <Typography>
        <h1 style={h1style} id="partC">
          PART C - THE HANDBOOK
        </h1>
        <h2 style={h2style} id="organizationHeading">
          <span style={{ fontSize: "24px" }}>2. </span>
          ORGANISATION AND DESIGN OF SPACES OF THE COURT COMPLEX
        </h2>
        <h3 id="facilitiesForAdvocatesHeading" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.6. </span>
          Facilities for Advocates
        </h3>

        <h3 id="advocatesBarHeading" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.6.</span>A. Advocates’ Bar
          Association
        </h3>
        <p style={paraStyle}>
          The Advocates’ Act (1961) calls for Bar Associations to be promoted by
          the State Bar Councils, for effective implementation of the Act. Bar
          Associations are present in every District Court Complex, and provide
          a fraternity for lawyers and their clerks/other staff to have
          discussions, render legal assistance, and make use of professional and
          recreational facilities provided by the Bar.
        </p>
        <p style={paraStyle}>
          A specific design for the Advocates’ Bar Association has not been
          provided in this Handbook, as the design is beyond the scope of the
          spaces designed for and built by the High Court, and may vary
          depending on the needs of the Bar Association in a specific
          jurisdiction. However, this section outlines some design
          considerations which must be kept in mind to ensure that the
          Association facilities cater to the needs of all users, and ensure a
          comfortable working environment to aid lawyers in effectively
          discharging their duties.
        </p>

        {getTextContentHeading(ObjectivesObject)}
        {getTextContentHeading(UsersObject)}
        {getTextContentHeading(AdjacenciesObject)}
        {subHeadingContent(DesignConsiderationsObject)}

        <h2 ref={referencesRef} id="advocatesBarRef" style={referenceStyle}>
          References
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
};

export default AdvocatesBarAssociation;
